"use client";
import React, { useState } from "react";
import { FaDownload, FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "motion/react";
import { Spotlight } from "@/components/ui/spotlight";
import { BackgroundBeams } from "@/components/ui/background-beams";
import { FlipWords } from "@/components/ui/flip-words";
import { TextGenerateEffect } from "@/components/ui/text-generate-effect";
import { HoverBorderGradient } from "@/components/ui/hover-border-gradient";
import { Button as MovingBorderButton } from "@/components/ui/moving-border";
import { personalData } from "@/utils/data/personal-data";
import TerminalBlock from "./TerminalBlock";

function HeroSection() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const resumeId = personalData.resume.split("/")[5];

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16">
      <Spotlight className="-top-40 left-0 md:left-60 md:-top-20" fill="#8b5cf6" />
      <div className="pointer-events-none absolute inset-0 bg-grid-white/[0.04] [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />
      <BackgroundBeams className="opacity-60" />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 sm:px-8 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] items-center gap-10 lg:gap-14">
        <div className="max-w-xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm uppercase tracking-[0.28em] text-violet-400 mb-4"
          >
            {personalData.address} · Available for work
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold leading-tight"
          >
            {personalData.shortName}
          </motion.h1>

          <div className="mt-4 text-2xl md:text-4xl font-semibold text-neutral-300 flex items-baseline gap-2">
            <span className="shrink-0">I&apos;m a</span>
            <FlipWords
              words={personalData.roles}
              className="bg-gradient-to-r from-cyan-400 to-violet-400 bg-clip-text text-transparent"
            />
          </div>

          <div className="mt-6 max-w-xl">
            <TextGenerateEffect words={personalData.description} />
          </div>

          <div className="flex flex-wrap gap-4 mt-8">
            <HoverBorderGradient
              as="a"
              href="#contact"
              containerClassName="rounded-full"
              className="bg-neutral-950 text-white flex items-center space-x-2 px-6 py-2.5"
            >
              Contact me
            </HoverBorderGradient>

            <MovingBorderButton
              as="button"
              onClick={() => setIsModalOpen(true)}
              borderRadius="1.75rem"
              containerClassName="h-12 w-auto min-w-[180px]"
              className="bg-neutral-950 border-slate-800 text-white px-6"
              borderClassName="bg-[radial-gradient(#8b5cf6_40%,transparent_60%)]"
            >
              <span className="flex items-center gap-2">
                View Resume <FaDownload className="text-sm" />
              </span>
            </MovingBorderButton>
          </div>
        </div>

        <div className="relative w-full flex justify-center lg:justify-end">
          <div className="pointer-events-none absolute -right-6 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-violet-500/25 blur-3xl" />
          <div className="pointer-events-none absolute right-10 top-8 h-40 w-40 rounded-full bg-cyan-400/20 blur-2xl" />

          <div className="relative w-full max-w-[26rem] rounded-2xl border border-white/10 bg-neutral-900/60 p-5 backdrop-blur-md">
            <p className="text-sm font-medium text-neutral-400">currently shipping</p>
            <div className="mt-4">
              <TerminalBlock />
            </div>
            <div className="flex justify-between items-center mt-5 w-full">
              <a
                href={personalData.github}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 rounded-full text-xs font-normal text-neutral-300 border border-white/10 hover:border-white/30"
              >
                GitHub →
              </a>
              <a
                href={personalData.linkedIn}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 rounded-full bg-white text-black text-xs font-bold"
              >
                LinkedIn
              </a>
            </div>
          </div>
        </div>
        </div>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              className="bg-neutral-950 rounded-2xl w-full max-w-4xl h-[80vh] relative border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-neutral-400 hover:text-white z-10"
                aria-label="Close resume"
              >
                <FaTimes size={22} />
              </button>
              <div className="absolute top-4 right-16 z-10">
                <a
                  href={`https://drive.google.com/uc?export=download&id=${resumeId}`}
                  download="ESLAM_AHMED_CV.pdf"
                  className="flex items-center gap-2 bg-violet-600 px-4 py-2 rounded-full text-white text-sm"
                >
                  <FaDownload className="text-sm" />
                </a>
              </div>
              <iframe
                src={`https://drive.google.com/file/d/${resumeId}/preview`}
                className="w-full h-full rounded-2xl"
                title="Resume"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default HeroSection;
